import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ClipboardList, MapPin, Zap, Building2, LineChart, CheckCircle2,
} from "lucide-react";

const STEPS = [
  {
    n: "01",
    icon: ClipboardList,
    title: "Contractor submits a request",
    body: "A contractor searching for your product category submits a structured quote request — with ZIP, product, quantity, timeline, and specs — directly from your website, a dealer site, or LumberLoop USA's public portal.",
  },
  {
    n: "02",
    icon: MapPin,
    title: "Territory + product matching",
    body: "The request is enriched with location data, then matched against your dealer network by state, ZIP proximity, and product line. Multiple channel rules can be layered in.",
  },
  {
    n: "03",
    icon: Zap,
    title: "Automated routing",
    body: "Approved dealer(s) in the contractor's territory are notified in real time with full lead context. Manufacturers see the routing decision in their dashboard.",
  },
  {
    n: "04",
    icon: Building2,
    title: "Dealer responds & quotes",
    body: "Dealers respond directly to the contractor. Response time, quote status, and outcome are all captured back to the platform — no reporting to chase down.",
  },
  {
    n: "05",
    icon: LineChart,
    title: "Manufacturer sees the outcome",
    body: "Your dashboard shows response times, quote activity, conversion rates, top-performing dealers, and lost-opportunity reasons — down to the ZIP and product line.",
  },
];

export default function HowItWorks() {
  useEffect(() => {
    document.title = "How It Works — LumberLoop USA";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute("content", "How LumberLoop USA's demand intelligence platform captures contractor demand, routes it through your dealer network, and reports the outcome.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="how-it-works-page">
      <section className="relative bg-[#0F2540] text-white border-b-2 border-[#1A365D]">
        <div className="stripes-stars absolute inset-0 opacity-15" aria-hidden="true"/>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-tag text-slate-300">How It Works</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-3 leading-[0.95]">
            Five steps from contractor demand <span className="text-[#C62828]">to measurable revenue.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-3xl">
            No new software for your dealers to learn. No marketplace competing with your channel. Just intelligent routing on top of the network you already have.
          </p>
        </div>
        <div className="h-2 stripes-flag" aria-hidden="true"/>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
        <div className="space-y-6">
          {STEPS.map((s, i) => (
            <div key={s.n} className="brutal-card bg-white p-6 md:p-8 flex flex-col md:flex-row gap-6" data-testid={`step-${s.n}`}>
              <div className="md:w-1/4">
                <div className="font-mono-ll text-[10px] tracking-[0.3em] uppercase text-slate-500">Step {s.n}</div>
                <div className="w-14 h-14 bg-[#C62828] text-white flex items-center justify-center mt-2"><s.icon size={26}/></div>
              </div>
              <div className="md:w-3/4">
                <h2 className="font-heading text-xl md:text-2xl font-bold uppercase text-[#0F2540]">{s.title}</h2>
                <p className="mt-2 text-slate-700 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FB] border-y border-slate-200 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="label-tag justify-center">What's Included Out of the Box</div>
          <h2 className="font-heading text-3xl md:text-4xl font-black uppercase mt-2 text-[#1A365D]">Everything the channel needs. <span className="text-[#C62828]">Nothing they don't.</span></h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-3 text-left">
            {[
              "Contractor quote request forms (embeddable)",
              "ZIP-based territory routing",
              "Approved-dealer network management",
              "Manufacturer + dealer dashboards",
              "Bulk CSV dealer upload",
              "Email notifications (Resend-powered)",
              "Response time + conversion tracking",
              "Lost-reason capture & reporting",
              "Product interest heat maps",
              "Multi-brand distributor visibility",
            ].map(it => (
              <div key={it} className="flex items-start gap-2"><CheckCircle2 size={18} className="text-[#C62828] mt-0.5 flex-shrink-0"/><span className="text-slate-700">{it}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0F2540] text-white py-20 border-t-2 border-[#1A365D] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} aria-hidden="true"/>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase leading-tight">Ready to see it on your <span className="text-[#C62828]">product lines?</span></h2>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">Request a Demo <ArrowRight size={16}/></Link>
            <Link to="/how-it-works" className="btn-ghost-light inline-flex items-center gap-2">See How It Works</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
